import { FederatedPointerEvent, Graphics, Rectangle, Sprite, Text } from 'pixi.js';
import { DropShadowFilter } from 'pixi-filters';
import { EVENT_NAMES } from '@/constants';
import { textureManager } from '@/managers/TextureManager';
import type { ElementLoadOptions } from '@/types';
import { resolveAppStaticPath } from '@/utils/staticAsset';
import { emitter } from '@/utils/emitter';
import { Element } from './Element.class';
import { PixiGlobal } from '../PixiGlobal';

const REPLAY_ICON_PATH = '/images/tail-page/replay@4x.svg';
const DEFAULT_BACKGROUND_COLOR = '#A050FF';
const REPLAY_BUTTON_ASPECT_RATIO = 182 / 52;
const REPLAY_ICON_SIZE_RATIO = 12 / 52;
const REPLAY_ICON_CENTER_X_RATIO = 54 / 182;
const REPLAY_TEXT_CENTER_X_RATIO = 102 / 182;
const REPLAY_TEXT_FONT_SIZE_RATIO = 16 / 52;

export class EventElement extends Element {
  private readonly background = new Graphics();
  private replayIcon: Sprite | null = null;
  private replayText: Text | null = null;
  private replayIconLoaded = false;
  private dropShadowFilter: DropShadowFilter | null = null;

  constructor() {
    super();
    this.background.eventMode = 'static';
    this.background.cursor = 'pointer';
    this.setTarget(this.background);
    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.on('pointerdown', this.handlePointerEvent);
    this.on('pointerup', this.handlePointerEvent);
    this.on('pointertap', this.handlePointerTap);
  }

  public override async load(options?: ElementLoadOptions): Promise<boolean> {
    this.attrs.originWidth = this.attrs.width;
    this.attrs.originHeight = this.attrs.height;

    if (this.data.config?.tailPageRole === 'replayButton') {
      const loaded = await this.loadReplayContent(options);
      if (!loaded) return false;
    }

    this.updateDropShadow();
    this.applyAttrs();
    return Boolean(this.data.config?.emitterEventName);
  }

  private async loadReplayContent(options?: ElementLoadOptions): Promise<boolean> {
    const replayIconUrl = resolveAppStaticPath(REPLAY_ICON_PATH);
    try {
      const texture = await textureManager.loadTexture(replayIconUrl, {
        signal: options?.signal,
        timeoutMs: options?.timeoutMs
      });
      texture.source.scaleMode = 'linear';
      this.replayIconLoaded = true;
      this.replayIcon = new Sprite(texture);
      this.replayIcon.anchor.set(0.5);
      this.replayIcon.eventMode = 'static';
      this.replayIcon.cursor = 'pointer';
      this.setTarget(this.replayIcon);

      this.replayText = new Text({
        text: '再看一遍',
        // 与 TextElement 一致：渲染器分辨率 × 2；避免再 ×2 + mipmap 在缩小时发糊
        resolution: PixiGlobal.getTextResolution(),
        autoGenerateMipmaps: false,
        roundPixels: true,
        style: {
          align: 'center',
          fill: '#FFFFFF',
          fontFamily: 'SourceHanSansCN',
          fontWeight: '500'
        }
      });
      this.replayText.anchor.set(0.5);
      this.replayText.eventMode = 'static';
      this.replayText.cursor = 'pointer';
      this.setTarget(this.replayText);
      return true;
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        console.error(`EventElement replay icon load failed: ${error}`);
      }
      return false;
    }
  }

  /**
   * 阅读端按 displayScale 在显示像素下重绘，保持 scale=1，
   * 避免「设计尺寸描字 + 容器拉伸」导致按钮文字发糊。
   */
  public override setSize(width: number, height: number): void {
    if (!this.attrs) return;
    if (this.attrs.width !== width) this.attrs.width = width;
    if (this.attrs.height !== height) this.attrs.height = height;

    this.scale.set(1, 1);
    if (this.attrs.scaleX !== 1) this.attrs.scaleX = 1;
    if (this.attrs.scaleY !== 1) this.attrs.scaleY = 1;

    this.redraw();
  }

  private updateDropShadow(displayScale = 1): void {
    this.dropShadowFilter?.destroy();
    this.dropShadowFilter = null;

    const shadow = this.data.config?.dropShadow;
    if (!shadow) {
      this.background.filters = [];
      return;
    }

    const rendererResolution = PixiGlobal.app?.renderer.resolution ?? 1;
    this.dropShadowFilter = new DropShadowFilter({
      color: shadow.color,
      alpha: shadow.alpha,
      blur: shadow.blur * displayScale,
      offset: { x: shadow.offsetX * displayScale, y: shadow.offsetY * displayScale },
      quality: 4,
      resolution: rendererResolution * 2
    });
    this.dropShadowFilter.antialias = 'on';
    this.background.filters = [this.dropShadowFilter];
  }

  protected override redraw(): void {
    super.redraw();

    const ds = this.attrs.layout?.displayScale ?? 1;
    // 步骤1：按显示像素计算按钮几何（不再依赖 Container.scale）
    const width = Math.max(1, (this.attrs.width ?? this.originWidth) * ds);
    const height = Math.max(1, (this.attrs.height ?? this.originHeight) * ds);
    const baseRadius = this.data.config?.borderRadius ?? (this.attrs.height ?? this.originHeight) / 2;
    const radius = Math.min(baseRadius * ds, width / 2, height / 2);

    this.background
      .clear()
      .roundRect(-width / 2, -height / 2, width, height, radius)
      .fill(this.data.config?.backgroundColor ?? DEFAULT_BACKGROUND_COLOR);
    this.hitArea = new Rectangle(-width / 2, -height / 2, width, height);
    this.layoutReplayContent(width, height);
    // 步骤2：阴影随显示尺寸更新
    this.updateDropShadow(ds);
  }

  private layoutReplayContent(width: number, height: number): void {
    if (!this.replayIcon || !this.replayText) return;

    const contentHeight = Math.min(height, width / REPLAY_BUTTON_ASPECT_RATIO);
    const iconSize = Math.max(1, Math.round(contentHeight * REPLAY_ICON_SIZE_RATIO));
    this.replayIcon.setSize(iconSize, iconSize);
    this.replayIcon.position.set(
      Math.round(-width / 2 + width * REPLAY_ICON_CENTER_X_RATIO),
      0
    );

    // 字号取整后重绘，避免小数 px Canvas 文字发糊
    this.replayText.style.fontSize = Math.max(
      1,
      Math.round(contentHeight * REPLAY_TEXT_FONT_SIZE_RATIO)
    );
    this.replayText.position.set(
      Math.round(-width / 2 + width * REPLAY_TEXT_CENTER_X_RATIO),
      0
    );
  }

  private readonly handlePointerEvent = (event: FederatedPointerEvent) => {
    event.stopPropagation();
    if ('stopPropagation' in event.nativeEvent) {
      event.nativeEvent.stopPropagation();
    }
  };

  private readonly handlePointerTap = (event: FederatedPointerEvent) => {
    this.handlePointerEvent(event);
    this.emitConfiguredEvent();
  };

  public emitConfiguredEvent(): void {
    if (this.data.config?.emitterEventName === EVENT_NAMES.BOOK_REPLAY) {
      emitter.emit(EVENT_NAMES.BOOK_REPLAY);
    }
  }

  public override destroy(): void {
    this.off('pointerdown', this.handlePointerEvent);
    this.off('pointerup', this.handlePointerEvent);
    this.off('pointertap', this.handlePointerTap);
    this.background.filters = [];
    this.dropShadowFilter?.destroy();
    this.dropShadowFilter = null;
    if (this.replayIconLoaded) {
      textureManager.release(resolveAppStaticPath(REPLAY_ICON_PATH));
      this.replayIconLoaded = false;
    }
    this.replayIcon = null;
    this.replayText = null;
    super.destroy();
  }
}
